import React, { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { fmtDT, fmtD } from "@/components/vault/common";
import { ReturnDialog } from "@/components/vault/VaultDialogs";

const STATES = [["open", "Currently out"], ["overdue", "Overdue"], ["returned", "Returned"], ["all", "All"]];

export default function VaultRegister() {
  const [sp] = useSearchParams();
  const [state, setState] = useState(sp.get("state") || "open");
  const [rows, setRows] = useState(null);
  const [ret, setRet] = useState(null);
  const load = useCallback(() => api.get("/vault/issues", { params: { state } }).then((r) => setRows(r.data)).catch((e) => toast.error(formatErr(e))), [state]);
  useEffect(() => { load(); }, [load]);
  const resolve = async (r) => { try { await api.post(`/vault/issues/${r.id}/resolve-reminder`); toast.success("Reminder resolved"); load(); } catch (e) { toast.error(formatErr(e)); } };
  const td = "px-3 py-2 border-t border-slate-100 align-top text-xs";
  return (
    <div data-testid="vault-register-page" className="space-y-5">
      <div>
        <div className="text-[11px] uppercase tracking-wider text-slate-500">File Vault</div>
        <h1 className="text-3xl md:text-4xl font-semibold tracking-tight">Issue & return register</h1>
        <p className="text-sm text-slate-500 mt-1">Outward (issued) and inward (returned) movement of physical files. Issue a file from its page.</p>
      </div>
      <div className="inline-flex bg-slate-100 rounded-md p-1">
        {STATES.map(([k, l]) => <button key={k} type="button" data-testid={`vault-reg-${k}`} onClick={() => setState(k)} className={`px-3 py-1.5 text-xs rounded-sm ${state === k ? "bg-white shadow-sm text-blue-700 font-semibold" : "text-slate-600"}`}>{l}</button>)}
      </div>
      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <table className="w-full text-sm min-w-[1000px]">
          <thead className="text-[11px] uppercase tracking-wider text-slate-500 bg-slate-50"><tr>{["File", "Issued to", "Purpose", "Issued", "Expected return", "Returned", ""].map((h) => <th key={h} className="text-left px-3 py-2">{h}</th>)}</tr></thead>
          <tbody>
            {(rows || []).map((r) => (
              <tr key={r.id} data-testid={`vault-reg-row-${r.id}`} className={r.overdue ? "bg-rose-50/50" : ""}>
                <td className={td}><Link to={`/vault/files/${r.file_id}`} className="mono text-blue-700">{r.file_number}</Link><div className="text-slate-700 text-sm">{r.file_name}</div></td>
                <td className={td}><b className="text-sm font-medium">{r.recipient_name}</b><div className="text-slate-500">{[r.recipient_org, `by ${r.issued_by_name}`].filter(Boolean).join(" · ")}</div></td>
                <td className={td}>{r.purpose}</td><td className={td}>{fmtDT(r.issued_at)}</td>
                <td className={td}>{fmtD(r.expected_return)}{r.overdue && <div className="text-rose-700 font-semibold" data-testid={`vault-reg-overdue-${r.id}`}>{r.days_overdue} days overdue{r.reminder_resolved ? " · reminder resolved" : ""}</div>}</td>
                <td className={td}>{r.returned_at ? <>{fmtDT(r.returned_at)}<div className="text-slate-500">{r.condition} · by {r.received_by_name}</div></> : "—"}</td>
                <td className={`${td} text-right whitespace-nowrap`}>
                  {!r.returned_at && <Button size="sm" data-testid={`vault-reg-receive-${r.id}`} onClick={() => setRet(r)} className="h-7 text-xs bg-emerald-700 hover:bg-emerald-800 text-white">Receive</Button>}
                  {r.overdue && !r.reminder_resolved && r.can_resolve && <Button size="sm" variant="outline" data-testid={`vault-reg-resolve-${r.id}`} onClick={() => resolve(r)} className="h-7 text-xs ml-1">Resolve reminder</Button>}
                </td>
              </tr>
            ))}
            {rows?.length === 0 && <tr><td colSpan={7} className="py-8 text-center text-slate-500" data-testid="vault-reg-empty">Nothing here.</td></tr>}
          </tbody>
        </table>
      </div>
      <ReturnDialog open={!!ret} onClose={() => setRet(null)} onDone={load} issue={ret} />
    </div>
  );
}
